import type { ShapeType } from '@/lib/shapes/types/shape-type'

/**
 * Click-to-place size for each shape type (drag-create takes its size from the drag instead).
 *
 * **Every polygon/star default is square, and must stay square.** These are *geometric* boxes — the
 * generator rect — and `regularPolygonCommands` inscribes vertices in the **ellipse** centred inside
 * it. An ellipse is only a circle when width === height, and only a circle yields a *regular*
 * (equilateral) polygon; a non-square box silently stretches the shape along one axis.
 *
 * The polygon entries used to be deliberately non-square (triangle 72×63, hexagon 72×83, star 72×68,
 * starone/startwo 72×76). Those were correct for this codebase's *older* generator, which stretched
 * vertices to touch all four edges of the box — under it the geometric box was also the drawn box, so
 * e.g. triangle 72×63 drew an equilateral triangle (base 72, height 72·√3/2 ≈ 63) and hexagon 72×83 a
 * regular pointy-top hexagon (83 ≈ 72 / cos30°). When the generator switched to ellipse-inscribed to
 * match legacy NSD, the compensation stopped being a compensation and became a second distortion
 * stacked on the first: a triangle then drew 0.866·72 wide by 0.75·63 = 47.25 tall — both squashed
 * out of equilateral and noticeably shorter than intended.
 *
 * With a square box the drawn triangle is 62.35 × 54, whose 1.1547 ratio is exactly 2/√3 — equilateral.
 * Asserted in shape-types.test.ts; see `regularPolygonVisualInsetRatios` for the ratio math.
 */
export const DEFAULT_SHAPE_SIZES: Record<ShapeType, { width: number; height: number }> = {
  square: { width: 72, height: 72 },
  circle: { width: 72, height: 72 },
  triangle: { width: 72, height: 72 },
  hexagon: { width: 72, height: 72 },
  pentagon: { width: 72, height: 72 },
  star: { width: 72, height: 72 },
  heart: { width: 72, height: 72 },
  starone: { width: 72, height: 72 },
  startwo: { width: 72, height: 72 },
  path: { width: 100, height: 100 },
}
